import React, { useState, useEffect } from 'react';
import { RefreshCw, Edit, Trash } from 'lucide-react';
import { toast } from 'react-toastify';
import { fetchMenu, syncMenu } from '../services/api.ts';
import { MenuItem } from '../types';
import api from '../../../../services/api';
import Layout from '../components/layout/Layout.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';

const Menu = () => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [categories, setCategories] = useState<{ id: number, nome: string }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Tudo');
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    price: '',
    available: true,
    isAddon: false,
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    available: true,
    isAddon: false,
  });
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchMenu();
        setMenuItems(data);
        setSelectedCategory('Tudo');
      } catch (error) {
        console.error('Error fetching menu:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    fetchCategories();
    // Carrega cardápio/categorias só no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/atendabot/categorias');
      setCategories(res.data);
    } catch (e) {
      setCategories([]);
    }
  };

  const formatarReais = (valor: string) => {
    let v = valor.replace(/\D/g, '');
    if (!v) v = '0';
    v = (parseInt(v, 10) / 100).toFixed(2);
    return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  const openEditModal = (item: MenuItem) => {
    setEditingItem(item);
    setEditForm({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      available: item.available,
      isAddon: item.isAddon ?? false,
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingItem) return;

    try {
      await api.put(`/atendabot/cardapio/${editingItem.id}`, {
        nome: editForm.name,
        descricao: editForm.description,
        preco: Number(editForm.price.replace(/[^\d,]/g, '').replace(',', '.')),
        disponivel: editForm.available,
        adicional: editForm.isAddon
      });

      setMenuItems((items) =>
        items.map((it) =>
          it.id === editingItem.id
            ? {
                ...it,
                name: editForm.name,
                description: editForm.description,
                price: Number(editForm.price.replace(/[^\d,]/g, '').replace(',', '.')),
                available: editForm.available,
                isAddon: editForm.isAddon
              }
            : it
        )
      );
      setEditModalOpen(false);
      setEditingItem(null);
      toast.success('Item atualizado.');
    } catch (e) {
      toast.error('Erro ao salvar item.');
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingItem(null);
  };

  const handleSyncMenu = async () => {
    try {
      setIsSyncing(true);
      const updatedMenu = await syncMenu();
      setMenuItems(updatedMenu);
      setSyncSuccess(true);
      setTimeout(() => setSyncSuccess(false), 3000);
    } catch (error) {
      console.error('Error syncing menu:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDelete = (itemId: string) => {
    setDeleteTargetId(itemId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    setIsDeleting(true);
    try {
      await api.delete(`/atendabot/cardapio/${deleteTargetId}`);
      setMenuItems(items => items.filter(item => item.id !== deleteTargetId));
      setDeleteTargetId(null);
      toast.success('Item removido.');
    } catch (e) {
      toast.error('Erro ao excluir item.');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteTarget = deleteTargetId
    ? menuItems.find(item => item.id === deleteTargetId) || null
    : null;

  // Corrige o filtro para usar nome da categoria
  const filteredItems =
    selectedCategory === 'Tudo'
      ? menuItems
      : menuItems.filter(item => item.category === selectedCategory);

  // Modal de criação
  const handleCreateItem = async () => {
    try {
      await api.post('/atendabot/cardapio', {
        nome: createForm.name,
        descricao: createForm.description,
        preco: Number(createForm.price.replace(/[^\d,]/g, '').replace(',', '.')),
        categoria_id: createForm.categoryId,
        disponivel: createForm.available,
        adicional: createForm.isAddon
      });
      setCreateModalOpen(false);
      setCreateForm({
        name: '',
        description: '',
        price: '',
        categoryId: '',
        available: true,
        isAddon: false
      });
      const data = await fetchMenu();
      setMenuItems(data);
      fetchCategories();
      toast.success('Item criado.');
    } catch (e) {
      toast.error('Erro ao criar item.');
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* MODAL DE CRIAÇÃO */}
      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Novo Item</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium">Nome</label>
                <input
                  className="mt-1 w-full border rounded p-2"
                  value={createForm.name}
                  onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Descrição</label>
                <textarea
                  className="mt-1 w-full border rounded p-2"
                  value={createForm.description}
                  onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Preço</label>
                <input
                  className="mt-1 w-full border rounded p-2"
                  value={createForm.price}
                  onChange={e => {
                    // Só dígitos para formatar como reais
                    let digitos = e.target.value.replace(/\D/g, '');
                    if (!digitos) digitos = '0';
                    const valorFormatado = formatarReais(digitos);
                    setCreateForm({ ...createForm, price: valorFormatado });
                  }}
                  placeholder="R$ 0,00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Categoria</label>
                <select
                  className="mt-1 w-full border rounded p-2"
                  value={createForm.categoryId}
                  onChange={e => setCreateForm({ ...createForm, categoryId: e.target.value })}
                >
                  <option value="">Selecione</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="create-available"
                  type="checkbox"
                  checked={createForm.available}
                  onChange={e => setCreateForm({ ...createForm, available: e.target.checked })}
                />
                <label htmlFor="create-available">Disponível</label>
                <input
                  id="create-addon"
                  type="checkbox"
                  checked={createForm.isAddon}
                  onChange={e => setCreateForm({ ...createForm, isAddon: e.target.checked })}
                  className="ml-4"
                />
                <label htmlFor="create-addon">Adicional</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-800"
                onClick={() => setCreateModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 rounded bg-[#2f7d72] text-white"
                onClick={handleCreateItem}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EDIÇÃO */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Editar Item</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium">Nome</label>
                <input
                  className="mt-1 w-full border rounded p-2"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Descrição</label>
                <textarea
                  className="mt-1 w-full border rounded p-2"
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm({ ...editForm, description: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Preço</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9,\.]*"
                  className="mt-1 w-full border rounded p-2"
                  value={editForm.price}
                  onChange={e => {
                    let digitos = e.target.value.replace(/\D/g, '');
                    if (!digitos) digitos = '0';
                    const valorFormatado = formatarReais(digitos);
                    setEditForm({ ...editForm, price: valorFormatado });
                  }}
                />
              </div>
              <div className="flex items-center gap-6 mt-4">
                {/* Switch Disponível */}
                <label className="flex items-center cursor-pointer">
                  <span className="mr-2">Disponível</span>
                  <span className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={editForm.available}
                      onChange={e => setEditForm({ ...editForm, available: e.target.checked })}
                    />
                    <span className={`block w-10 h-6 rounded-full transition-colors duration-300 ${editForm.available ? 'bg-[#2f7d72]' : 'bg-gray-300'}`}></span>
                    <span
                      className={`absolute left-0 top-0 w-6 h-6 rounded-full bg-white shadow transform transition-transform duration-300 ${editForm.available ? 'translate-x-4' : ''}`}
                    ></span>
                  </span>
                </label>
                {/* Switch Adicional */}
                <label className="flex items-center cursor-pointer">
                  <span className="mr-2">Adicional</span>
                  <span className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={editForm.isAddon}
                      onChange={e => setEditForm({ ...editForm, isAddon: e.target.checked })}
                    />
                    <span className={`block w-10 h-6 rounded-full transition-colors duration-300 ${editForm.isAddon ? 'bg-[#2f7d72]' : 'bg-gray-300'}`}></span>
                    <span
                      className={`absolute left-0 top-0 w-6 h-6 rounded-full bg-white shadow transform transition-transform duration-300 ${editForm.isAddon ? 'translate-x-4' : ''}`}
                    ></span>
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-800"
                onClick={handleEditCancel}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 rounded bg-[#2f7d72] text-white"
                onClick={handleEditSave}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      <Layout>
        <ConfirmModal
          open={deleteTargetId != null}
          variant="destructive"
          title="Excluir item do cardápio?"
          description="O item será removido permanentemente do cardápio."
          preview={deleteTarget ? (
            <div>
              <div className="font-semibold text-slate-900">{deleteTarget.name}</div>
              {deleteTarget.description && (
                <div className="mt-1 text-xs text-slate-500">{deleteTarget.description}</div>
              )}
            </div>
          ) : null}
          confirmLabel={isDeleting ? 'Excluindo...' : 'Excluir item'}
          saving={isDeleting}
          onCancel={() => { if (!isDeleting) setDeleteTargetId(null); }}
          onConfirm={handleDeleteConfirm}
        />
        <div className="space-y-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-amber-900">
            <div className="flex items-start gap-3">
              <div>
                <p className="text-sm font-semibold">Integração com OneMenu</p>
                <p className="mt-1 text-sm leading-relaxed">
                  Você deve cadastrar o cardápio diretamente no OneMenu e usar o mesmo conteúdo
                  aqui no Atendabot. Para isso, ative o OneMenu como origem do cardápio na
                  configuração de atendimento com IA do Atendabot.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center gap-3 justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-800">Cardápio Digital</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Gerencie os itens do seu cardápio digital.
                </p>
              </div>
        <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
          <button
            onClick={handleSyncMenu}
            disabled={isSyncing}
            className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2f7d72] hover:bg-[#27685f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 w-full sm:w-auto justify-center"
          >
                  {isSyncing ? (
                    <div className="w-5 h-5 rounded-full animate-spin border-2 border-solid border-white border-t-transparent"></div>
                  ) : (
                    <>
                      <RefreshCw size={16} className="mr-2" />
                      Atualizar Cardápio
                    </>
                  )}
                </button>
          <button
            onClick={() => { setCreateModalOpen(true); fetchCategories(); }}
            className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
          >
            + Novo Item
          </button>
              </div>
            </div>

            {syncSuccess && (
              <div className="px-6 py-3 bg-green-50 border-b border-green-200 text-sm text-green-600 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                Cardápio atualizado com sucesso!
              </div>
            )}

            {menuItems.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                Nenhum item no cardápio. Clique em "Atualizar Cardápio" para sincronizar.
              </div>
            ) : (
              <>
                {/* Category Tabs */}
                <div className="px-6 pt-4 pb-1 border-b border-gray-200 overflow-x-auto">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedCategory('Tudo')}
                      className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-2 ${
                        selectedCategory === 'Tudo'
                          ? 'bg-[#2f7d72] text-white border-teal-600'
                          : 'bg-gray-100 text-gray-700 border-transparent hover:bg-teal-50 hover:text-teal-600'
                      }`}
                    >
                      Tudo
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.nome)}
                        className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-2 ${
                          selectedCategory === cat.nome
                            ? 'bg-[#2f7d72] text-white border-teal-600'
                            : 'bg-gray-100 text-gray-700 border-transparent hover:bg-teal-50 hover:text-teal-600'
                        }`}
                      >
                        {cat.nome}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Menu Items */}
                <div className="p-6 space-y-6">
                  <div className="sm:hidden space-y-4">
                    {filteredItems.map((item) => (
                      <div key={`mobile-${item.id}`} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-base font-semibold text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-500 mt-1 break-words whitespace-normal">{item.description}</p>
                            <p className="text-sm font-semibold text-gray-900 mt-2">{currencyFormatter.format(item.price)}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className={`px-3 py-1 text-[11px] font-semibold rounded-full ${
                              item.available ? 'bg-[#2f7d72] text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {item.available ? 'Disponível' : 'Indisponível'}
                            </span>
                            <span className={`px-3 py-1 text-[11px] font-semibold rounded-full ${
                              item.isAddon ? 'bg-yellow-400 text-gray-900' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {item.isAddon ? 'Adicional' : 'Não'}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => openEditModal(item)}
                            className="flex-1 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:border-slate-300"
                          >
                            <Edit size={14} />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="flex-1 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 hover:border-rose-300"
                          >
                            <Trash size={14} />
                            Excluir
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="hidden sm:block">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Item
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Descrição
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Preço
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Disponível
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Adicional
                            </th>
                            <th className="relative px-6 py-3">
                              <span className="sr-only">Ações</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredItems.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                              </td>
                              <td className="px-6 py-4 max-w-[160px]">
                                <div className="text-sm text-gray-500 truncate">{item.description}</div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {currencyFormatter.format(item.price)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 text-xs leading-5 font-bold rounded-full transition-all shadow-sm ${
                                  item.available
                                    ? 'bg-[#2f7d72] text-white'
                                    : 'bg-gray-200 text-gray-600'
                                }`}>
                                  {item.available ? 'Disponível' : 'Indisponível'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 text-xs leading-5 font-bold rounded-full transition-all shadow-sm ${
                                  item.isAddon
                                    ? 'bg-yellow-400 text-gray-900'
                                    : 'bg-gray-200 text-gray-600'
                                }`}>
                                  {item.isAddon ? 'Adicional' : 'Não'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 font-medium rounded-md p-2 mr-2"
                                  onClick={() => openEditModal(item)}
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  className="bg-red-100 hover:bg-red-200 text-red-800 font-medium rounded-md p-2"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  <Trash size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
};

export default Menu;
